import React, { useEffect, useState } from 'react';
import productImage from '../assets/unfold.svg';
import logoImage from '../assets/logo.png';

const Icon = ({ path, className = "w-6 h-6" }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={path} />
  </svg>
);

const LandingPage = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  
  const alternatingWords = ['Health', 'Growth', 'Success', 'Focus', 'Energy'];

  useEffect(() => {
    document.title = 'Unfold - Transform Your Team\'s Wellness';
    
    // Animate alternating words
    const wordInterval = setInterval(() => {
      setCurrentWordIndex((prevIndex) => (prevIndex + 1) % alternatingWords.length);
    }, 3000);

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in-up');
        } else {
          // entry.target.classList.remove('animate-fade-in-up');
        }
      });
    }, { threshold: 0.1 });

    document.querySelectorAll('.section-animate').forEach(el => {
      observer.observe(el);
    });

    return () => {
      clearInterval(wordInterval);
      observer.disconnect();
    };
  }, []);

  const features = [
    {
      icon: <Icon path="M13 10V3L4 14h7v7l9-11h-7z" />,
      title: 'Wearable Data Analytics',
      description: 'Transform data from smartwatches and accelerometers into meaningful wellness insights for your team.'
    },
    {
      icon: <Icon path="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a2 2 0 01-2-2V7a2 2 0 012-2h2m4 0h-2m-2-2v2m-2 2h-2" />,
      title: 'Actionable Health Insights',
      description: 'Get data-driven recommendations based on activity, sleep, and stress patterns to improve team well-being.'
    },
    {
      icon: <Icon path="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />,
      title: 'Privacy-First Analytics',
      description: 'Analyze aggregated health data while maintaining complete individual privacy and anonymity.'
    },
  ];

  const howItWorksSteps = [
    {
      icon: <Icon path="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />,
      title: '1. Connect Your Devices',
      description: 'Integrate with popular smartwatches and fitness trackers. Team members opt-in to share anonymous health data.'
    },
    {
      icon: <Icon path="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />,
      title: '2. Analyze Health Patterns',
      description: 'Our platform continuously analyzes accelerometer and biometric data to identify wellness trends and risk factors.'
    },
    {
      icon: <Icon path="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />,
      title: '3. Access Team Insights',
      description: 'View aggregated health dashboards showing activity levels, stress patterns, and recovery metrics for informed decision-making.'
    }
  ];

  const faqs = [
    {
      question: 'What devices and data sources do you support?',
      answer: 'We integrate with popular smartwatches like Apple Watch, Fitbit, Garmin, and other devices with accelerometers. We analyze activity, sleep, heart rate variability, and movement patterns while keeping all data anonymous.'
    },
    {
      question: 'Is the health data really anonymous?',
      answer: 'Yes. Individual device data is never shared or identifiable. We aggregate and anonymize all biometric and activity data, requiring minimum participation thresholds before any insights are displayed.'
    },
    {
      question: 'What kind of health insights can I expect?',
      answer: 'You will gain insights into team activity levels, sleep quality patterns, stress indicators from heart rate variability, and recovery trends. Our analytics help identify potential burnout risks and wellness opportunities.'
    },
    {
      question: 'Do employees need to wear devices constantly?',
      answer: 'No. Employees can participate as much or as little as they want. Our platform is designed to work with varying levels of device usage while still providing meaningful team-level insights.'
    }
  ];

  const FAQItem = ({ q, a }) => {
    const [isOpen, setIsOpen] = useState(false);
    return (
      <div className="border-b border-gray-200 py-6">
        <dt>
          <button onClick={() => setIsOpen(!isOpen)} className="w-full flex justify-between items-center text-left text-gray-700">
            <span className="text-lg font-medium">{q}</span>
            <span className="ml-6 h-7 flex items-center">
              <Icon path={isOpen ? 'M18 12H6' : 'M12 6v12m6-6H6'} className={`w-5 h-5 transform transition-transform ${isOpen ? 'rotate-90' : ''}`} />
            </span>
          </button>
        </dt>
        <dd className={`mt-2 pr-12 overflow-hidden transition-all duration-500 ease-in-out ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
          <p className="text-base text-gray-600 pt-4">{a}</p>
        </dd>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm border-b border-gray-100 sticky top-0 z-50 shadow-sm">
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <a href="/" className="flex items-center space-x-3 group">
              <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                <img src={logoImage} alt="Unfold Logo" className="w-6 h-6 object-contain" />
              </div>
              <span className="text-xl font-semibold text-gray-900">unfold</span>
            </a>
            
            {/* Mobile menu button */}
            <div className="md:hidden">
              <button 
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className="text-gray-600 hover:text-gray-900 p-2 rounded-md"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth="2" 
                    d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
                  />
                </svg>
              </button>
            </div>
            
            {/* Desktop navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                Features
              </a>
              <a href="#how-it-works" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                How It Works
              </a>
              <a href="#faq" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                FAQ
              </a>
            </div>
            
            <div className="hidden sm:flex items-center space-x-4">
              <a href="/login" className="text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors">
                Sign In
              </a>
              <a href="/register" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium hover:from-blue-700 hover:to-blue-800 transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5">
                Get Started
              </a>
            </div>
            
            {/* Mobile CTA */}
            <div className="sm:hidden">
              <a href="/register" className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-1.5 rounded-lg text-sm font-medium shadow-lg">
                Start
              </a>
            </div>
          </div>
          
          {/* Mobile menu */}
          {isMobileMenuOpen && (
            <div className="md:hidden">
              <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t border-gray-200">
                <a 
                  href="#features" 
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  Features
                </a>
                <a 
                  href="#how-it-works" 
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  How It Works
                </a>
                <a 
                  href="#faq" 
                  className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  FAQ
                </a>
                <div className="pt-4 pb-3 border-t border-gray-200">
                  <div className="flex items-center px-3 space-y-2 flex-col">
                    <a 
                      href="/login" 
                      className="w-full text-center px-3 py-2 text-base font-medium text-gray-600 hover:text-gray-900 hover:bg-gray-50 rounded-md transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Sign In
                    </a>
                    <a 
                      href="/register" 
                      className="w-full text-center bg-blue-600 text-white px-3 py-2 rounded-md text-base font-medium hover:bg-blue-700 transition-colors"
                      onClick={() => setIsMobileMenuOpen(false)}
                    >
                      Get Started
                    </a>
                  </div>
                </div>
              </div>
            </div>
          )}
        </nav>
      </header>

      <main>
        {/* Hero Section */}
        <section className="bg-white">
          <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
            <div className="grid md:grid-cols-2 gap-16 items-center">
              {/* Text Content */}
              <div className="text-center md:text-left">
                <span className="inline-block bg-blue-100 text-blue-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
                  Team Wellness Analytics
                </span>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                  Transform Your Team's{' '}
                  <span className="text-blue-600 inline-block animate-pulse font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-blue-600 via-purple-600 to-blue-600">
                    {alternatingWords[currentWordIndex]}
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-xl mx-auto md:mx-0">
                  Get actionable insights from wearable devices to build healthier, more productive teams. Privacy-first analytics that help you understand and improve workplace wellness.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
                  <a href="/register" className="bg-blue-600 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm">
                    Start Free Trial
                  </a>
                  <a href="https://calendly.com/kings-tryunfold/30min?month=2025-08" target="_blank" rel="noopener noreferrer" className="border border-gray-300 text-gray-800 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-50 transition-colors">
                    View Demo
                  </a>
                </div>
              </div>

              {/* Image/Graphic */}
              <div className="flex justify-center mt-8 md:mt-0">
                <img src={productImage} alt="Unfold Analytics Dashboard" className="rounded-full shadow-lg max-w-xs sm:max-w-sm md:max-w-md w-full" />
              </div>
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wider mb-4">Trusted by 500+ Teams</h3>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">31%</div>
                <div className="text-sm text-gray-600">More Productive</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">40%</div>
                <div className="text-sm text-gray-600">Less Burnout</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">25%</div>
                <div className="text-sm text-gray-600">More Engaged</div>
              </div>
              <div>
                <div className="text-2xl font-bold text-gray-900 mb-1">50%</div>
                <div className="text-sm text-gray-600">Lower Turnover</div>
              </div>
            </div>
          </div>
        </section>

        {/* How It Works Section */}
        <section id="how-it-works" className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">How It Works</h2>
              <p className="text-lg text-gray-600">
                Transform wearable data into team health insights in three simple steps.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {howItWorksSteps.map((step, index) => (
                <div key={index} className="text-center group">
                  <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mx-auto mb-6 shadow-sm transition-all duration-300">
                    <div className="text-blue-600 text-xl">{step.icon}</div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="py-16 bg-white relative">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Features</h2>
                    <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                      Transform wearable device data into actionable insights.
                    </p>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {features.map((feature, index) => (
                        <div key={index} className="bg-white rounded-2xl p-8 shadow-sm hover:shadow-md transition-all duration-300 border border-gray-200 group">
                            <div className="flex items-center justify-center w-16 h-16 bg-blue-100 rounded-2xl mx-auto mb-6 transition-transform duration-300">
                                <div className="text-blue-600 text-xl">{feature.icon}</div>
                            </div>
                            <h3 className="text-xl font-bold text-gray-900 mb-4 text-center">{feature.title}</h3>
                            <p className="text-gray-600 leading-relaxed text-center">{feature.description}</p>
                        </div>
                    ))}
                </div>
            </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 bg-gray-50">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="text-center mb-12">
                    <h2 className="text-3xl font-bold text-gray-900 mb-4">Trusted by Leading Organizations</h2>
                    <p className="text-lg text-gray-600">See how teams are transforming their wellness culture with Unfold</p>
                </div>
                
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {/* Testimonial 1 */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                            <img className="w-10 h-10 rounded-full mr-3" src="https://i.pravatar.cc/150?u=sarah" alt="Sarah Johnson" />
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Sarah Johnson</p>
                                <p className="text-xs text-gray-600">VP People Operations, TechCorp</p>
                            </div>
                        </div>
                        <blockquote className="text-gray-700 text-sm leading-relaxed">
                            "Unfold's analytics gave us unprecedented visibility into our team's health patterns. We've seen a 40% reduction in burnout since implementation."
                        </blockquote>
                        <div className="flex text-yellow-400 mt-3">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                            ))}
                        </div>
                    </div>

                    {/* Testimonial 2 */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                            <img className="w-10 h-10 rounded-full mr-3" src="https://i.pravatar.cc/150?u=michael" alt="Michael Chen" />
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Michael Chen</p>
                                <p className="text-xs text-gray-600">Head of Wellness, FinanceFlow</p>
                            </div>
                        </div>
                        <blockquote className="text-gray-700 text-sm leading-relaxed">
                            "The sleep and stress insights helped us identify patterns we never saw before. Our team productivity increased by 28% in just 3 months."
                        </blockquote>
                        <div className="flex text-yellow-400 mt-3">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                            ))}
                        </div>
                    </div>

                    {/* Testimonial 3 */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                            <img className="w-10 h-10 rounded-full mr-3" src="https://i.pravatar.cc/150?u=emma" alt="Emma Rodriguez" />
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Emma Rodriguez</p>
                                <p className="text-xs text-gray-600">Chief People Officer, InnovateLabs</p>
                            </div>
                        </div>
                        <blockquote className="text-gray-700 text-sm leading-relaxed">
                            "The real-time mood tracking helped us catch early signs of team burnout. We've prevented 3 potential resignations this quarter alone."
                        </blockquote>
                        <div className="flex text-yellow-400 mt-3">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                            ))}
                        </div>
                    </div>

                    {/* Testimonial 4 */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                            <img className="w-10 h-10 rounded-full mr-3" src="https://i.pravatar.cc/150?u=david" alt="David Park" />
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">David Park</p>
                                <p className="text-xs text-gray-600">Operations Director, StartupHub</p>
                            </div>
                        </div>
                        <blockquote className="text-gray-700 text-sm leading-relaxed">
                            "Implementation was seamless. The team adopted it quickly and now we have data-driven conversations about wellness instead of guesswork."
                        </blockquote>
                        <div className="flex text-yellow-400 mt-3">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                            ))}
                        </div>
                    </div>

                    {/* Testimonial 5 */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                            <img className="w-10 h-10 rounded-full mr-3" src="https://i.pravatar.cc/150?u=lisa" alt="Lisa Thompson" />
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Lisa Thompson</p>
                                <p className="text-xs text-gray-600">HR Director, GrowthCorp</p>
                            </div>
                        </div>
                        <blockquote className="text-gray-700 text-sm leading-relaxed">
                            "The privacy-first approach gave our team confidence to participate. Now we have 95% adoption and actionable wellness insights."
                        </blockquote>
                        <div className="flex text-yellow-400 mt-3">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                            ))}
                        </div>
                    </div>

                    {/* Testimonial 6 */}
                    <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                        <div className="flex items-center mb-4">
                            <img className="w-10 h-10 rounded-full mr-3" src="https://i.pravatar.cc/150?u=alex" alt="Alex Kumar" />
                            <div>
                                <p className="font-semibold text-gray-900 text-sm">Alex Kumar</p>
                                <p className="text-xs text-gray-600">Team Lead, DevCorp</p>
                            </div>
                        </div>
                        <blockquote className="text-gray-700 text-sm leading-relaxed">
                            "Our remote team finally has visibility into wellness patterns. The insights helped us adjust meeting schedules and improve work-life balance."
                        </blockquote>
                        <div className="flex text-yellow-400 mt-3">
                            {[...Array(5)].map((_, i) => (
                                <svg key={i} className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                                </svg>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        {/* FAQ Section */}
        <section id="faq" className="py-16 bg-white">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Frequently Asked Questions</h2>
              <p className="text-lg text-gray-600">
                Everything you need to know about wearable health analytics.
              </p>
            </div>
            <dl className="space-y-2">
              {faqs.map((faq, index) => (
                <FAQItem key={index} q={faq.question} a={faq.answer} />
              ))}
            </dl>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Start Building Healthier Teams Today</h2>
              <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">Join 500+ organizations already using Unfold to improve team wellness and productivity through data-driven insights.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
                <a href="/register" className="bg-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-sm">Get Started Free</a>
                <a href="https://calendly.com/kings-tryunfold/30min?month=2025-08" target="_blank" rel="noopener noreferrer" className="border border-gray-300 text-gray-800 px-8 py-4 rounded-lg font-semibold hover:bg-gray-50 transition-colors">Book a Demo</a>
              </div>
              <div className="flex flex-wrap items-center justify-center gap-4 sm:gap-6 text-gray-500 text-sm">
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" /></svg>
                  5-minute setup
                </span>
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" /></svg>
                  No commitment
                </span>
                <span className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" /></svg>
                  Expert support
                </span>
              </div>
            </div>
          </div>
        </section>

        {/* Security Section */}
        <section className="py-16 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">Enterprise-Grade Security</h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">Your team's health data is protected with industry-leading security standards.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M2.166 4.999A11.954 11.954 0 0010 1.944 11.954 11.954 0 0017.834 5c.11.65.166 1.32.166 2.001 0 5.225-3.34 9.67-8 11.317C5.34 16.67 2 12.225 2 7c0-.682.057-1.35.166-2.001zm11.541 3.708a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" /></svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">HIPAA Compliant</h3>
                <p className="text-sm text-gray-600">Full HIPAA compliance for protected health information handling and storage.</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M18 8a6 6 0 01-7.743 5.743L10 14l-1 1-1 1H6v2H2v-4l4.257-4.257A6 6 0 1118 8zm-6-4a1 1 0 100 2 2 2 0 012 2 1 1 0 102 0 4 4 0 00-4-4z" /></svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">End-to-End Encryption</h3>
                <p className="text-sm text-gray-600">256-bit AES encryption for all data in transit and at rest.</p>
              </div>
              <div className="text-center">
                <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-lg mx-auto mb-4">
                  <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20"><path d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812z" /></svg>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">SOC 2 Certified</h3>
                <p className="text-sm text-gray-600">Type II SOC 2 certification for security, availability, and confidentiality.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
};

export default LandingPage;